import { useState, useEffect } from "react";
import { Button, Form, Input } from "antd";
import { useLocation, useNavigate } from "react-router";
import { connect } from "react-redux";

import { GetUrlParams, ObjectNumbersToEnglish } from "scripts/helpers";
import { updateUser } from "scripts/reducer";
import { Patterns } from "scripts/settings";
import API from "scripts/api";
import Icons from "scripts/icons";
import Resources from "scripts/resources";
import Rules from "scripts/rules";

import InputCode from "components/input-code";

const Component = ({ onUpdateUser }) => {
	const initialState = { countdown: 0, counter: 0 };
	const [state, setState] = useState(initialState);
	const { countdown, counter, submitting } = state;
	const [form] = Form.useForm();
	const { search } = useLocation();
	const navigate = useNavigate();
	const { mobile } = GetUrlParams(search);

	const handleCompare = (value) => {
		return !value || form.getFieldValue("password") === value
			? Promise.resolve()
			: Promise.reject(Resources.comparePass);
	};

	const handleCode = () => {
		if (!submitting) {
			setState((prevState) => ({ ...prevState, submitting: true }));

			API.auth
				.getOtp({ phone: mobile })
				.then(({ otpExpirationSeconds }) => {
					setState((prevState) => ({
						...prevState,
						submitting: false,
						countdown: otpExpirationSeconds,
						counter: otpExpirationSeconds,
					}));
				})
				.catch(() => {
					setState((prevState) => ({
						...prevState,
						submitting: false,
					}));
				});
		}
	};

	const handleCountdown = () => {
		const timeout = setTimeout(() => {
			setState((prevState) => ({
				...prevState,
				countdown: prevState.countdown - 1,
			}));
		}, 1000);

		if (countdown < 1) clearTimeout(timeout);
	};

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));

				values = ObjectNumbersToEnglish(values);

				API.auth
					.reset(values)
					.then(() => {
						API.auth
							.login({ phone: values.phone, password: values.password })
							.then(({ token, tokenExpirationDays, user }) => {
								API.setCookie(
									process.env.REACT_APP_COOKIE_TOKEN,
									token,
									tokenExpirationDays
								);
								onUpdateUser(user);
								navigate("/");
							})
							.catch(() => {
								setState((prevState) => ({
									...prevState,
									submitting: false,
								}));
							});
					})
					.catch(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					})
					.finally(() => {});
			});
		}
	};

	const componentDidMount = () => {
		if (Patterns.mobile.test(mobile)) {
			form.setFieldValue("phone", mobile);

			handleCode();
		} else {
			navigate("/auth/status", { replace: true });
		}
	};

	useEffect(handleCountdown, [countdown]);
	useEffect(componentDidMount, []);

	return (
		<>
			<h1 className="heading">{Resources.forgotPass}</h1>
			<span className="text">{Resources.verifyText}</span>
			<Form
				form={form}
				layout="vertical"
				requiredMark={false}
				autoComplete="off"
			>
				<Form.Item name="phone" label={Resources.mobile} rules={Rules.mobile}>
					<Input
						className="input-ltr"
						readOnly
						type="tel"
						size="large"
						placeholder={Resources.mobile}
						suffix={<Icons.Calling />}
					/>
				</Form.Item>
				<Form.Item name="code" label={Resources.code} rules={Rules.required}>
					<InputCode
						countdown={countdown}
						percent={100 - Math.round((countdown * 100) / counter)}
						submitting={submitting}
						onResend={handleCode}
					/>
				</Form.Item>
				<Form.Item
					name="password"
					label={Resources.password}
					rules={Rules.password}
				>
					<Input
						type="password"
						size="large"
						placeholder={Resources.password}
						prefix={<Icons.Lock />}
					/>
				</Form.Item>
				<Form.Item
					name="password_confirmation"
					label={`${Resources.confirm} ${Resources.password}`}
					rules={[
						...Rules.password,
						{
							validator: (rule, value) => handleCompare(value),
						},
					]}
				>
					<Input
						type="password"
						size="large"
						placeholder={`${Resources.confirm} ${Resources.password}`}
						prefix={<Icons.Lock />}
					/>
				</Form.Item>
			</Form>
			<div className="actions">
				<Button
					className="btn-default"
					size="large"
					loading={submitting}
					onClick={() => navigate("/auth/status", { replace: true })}
				>
					{`${Resources.edit} ${Resources.mobile}`}
				</Button>
				<Button
					type="primary"
					size="large"
					loading={submitting}
					onClick={handleSubmit}
				>
					{`${Resources.change} ${Resources.password}`}
				</Button>
			</div>
		</>
	);
};

export default connect(null, (dispatch) => {
	return {
		onUpdateUser: (data) => dispatch(updateUser(data)),
	};
})(Component);
